import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, XCircle, ShieldCheck, MessageSquare, Flame, Star, Zap,
  SearchCheck, ClipboardCheck, Building2, Landmark, Layers, Users, MoreHorizontal,
  Search, Filter, ArrowLeft, MapPin, ChevronDown, ExternalLink, Download
} from "lucide-react";
import { api, formatPrice, asItems, approveSoldVerification, rejectSoldVerification, formatApiError, prefixUrl, openPrivateDocument } from "@/lib/api";
import { toast } from "sonner";
import { isPendingSoldStatus, prettyLabel } from "@/lib/propertyUi";
import { WorkbenchError, WorkbenchLoading } from "@/components/admin/AdminWorkbenchUi";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const getImageUrl = (urlObj) => {
  if (!urlObj) return "";
  const url = typeof urlObj === "string" ? urlObj : (urlObj.url || urlObj.file_url || urlObj.public_url || "");
  if (!url) return "";
  return prefixUrl(url);
};

const formatAreaValue = (value, unit) => {
  if (value == null || value === "") return "—";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return `${amount.toLocaleString("en-IN")}${unit ? ` ${String(unit).replace(/_/g, " ")}` : ""}`;
};

function rawObject(item = {}) {
  try {
    if (typeof item.raw === "string") return JSON.parse(item.raw);
    if (item.raw && typeof item.raw === "object") return item.raw;
  } catch {
    return {};
  }
  return {};
}

function soldReviewFor(item = {}) {
  return item.sold_review || rawObject(item).sold_review || {};
}

export default function AdminApprovals() {
  const [properties, setProperties] = useState([]);
  const [agents, setAgents] = useState([]);
  const [plots, setPlots] = useState([]);
  const [ownerVerifications, setOwnerVerifications] = useState([]);
  const [activeTab, setActiveTab] = useState("properties");
  const [selectedId, setSelectedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeImage, setActiveImage] = useState(null);
  const [activeDoc, setActiveDoc] = useState(null);
  const [loadingDocId, setLoadingDocId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const handlePreviewDocument = async (rawUrl, docId, docTitle) => {
    const urlStr = typeof rawUrl === "string" ? rawUrl : (rawUrl?.url || rawUrl?.file_url || rawUrl?.public_url || "");
    if (!urlStr) return;

    setLoadingDocId(docId);
    try {
      if (urlStr.includes("/files/private/")) {
        let relativePath = urlStr.includes("http") ? new URL(urlStr).pathname : urlStr;
        relativePath = relativePath.replace(/^\/api(\/v1)?/, "");
        if (!relativePath.startsWith("/")) relativePath = "/" + relativePath;

        const response = await api.get(relativePath, { responseType: "blob" });
        const rawBlob = response.data;
        const isPdf = urlStr.endsWith(".pdf") || (response.headers["content-type"] || "").includes("pdf");

        const typedBlob = new Blob([rawBlob], { type: isPdf ? "application/pdf" : (rawBlob.type || "image/jpeg") });
        const blobUrl = URL.createObjectURL(typedBlob);
        const fileName = `${(docTitle || "document").toLowerCase().replace(/\s+/g, "_")}${isPdf ? ".pdf" : ".jpg"}`;

        setActiveDoc({ url: blobUrl, title: docTitle || "Verification Document", isPdf, fileName, originalUrl: prefixUrl(urlStr) });
      } else {
        const fullUrl = prefixUrl(urlStr);
        const isPdf = /\.pdf(?:$|[?#])/i.test(fullUrl);
        const fileName = `${(docTitle || "document").toLowerCase().replace(/\s+/g, "_")}${isPdf ? ".pdf" : ".jpg"}`;
        setActiveDoc({ url: fullUrl, title: docTitle || "Verification Document", isPdf, fileName, originalUrl: fullUrl });
      }
    } catch (err) {
      toast.error("Failed to load document: " + formatApiError(err));
    } finally {
      setLoadingDocId(null);
    }
  };

  // Filter States
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCity, setFilterCity] = useState("all");

  const [fullDetails, setFullDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [detailsReloadKey, setDetailsReloadKey] = useState(0);

  useEffect(() => {
    if (!selectedId) {
      setFullDetails(null);
      setDetailsError("");
      return;
    }
    if (activeTab === "properties") {
      setLoadingDetails(true);
      setDetailsError("");
      Promise.allSettled([
        api.get(`/v1/admin/property-explorer/${selectedId}`),
        api.get(`/properties/${selectedId}`)
      ])
        .then(([explorerRes, propRes]) => {
          const explorerData = explorerRes.status === "fulfilled" ? (explorerRes.value?.data || {}) : {};
          const propData = propRes.status === "fulfilled" ? (propRes.value?.data || {}) : {};

          // Extract documents and images, preferring items that have a non-empty url
          const expProperty = explorerData.property || {};
          const mergedProperty = { ...expProperty, ...propData };

          const combinedDocs = [
            ...(Array.isArray(explorerData.documents) ? explorerData.documents : []),
            ...(Array.isArray(propData.documents) ? propData.documents : []),
            ...(Array.isArray(expProperty.documents) ? expProperty.documents : []),
          ];
          
          // Deduplicate documents by id or document_type, preserving the one with a url
          const docMap = new Map();
          for (const d of combinedDocs) {
            if (!d) continue;
            const key = d.id || d.document_type || JSON.stringify(d);
            const existing = docMap.get(key);
            if (!existing || (!existing.url && d.url)) {
              docMap.set(key, d);
            }
          }
          const documents = Array.from(docMap.values());

          const images = Array.isArray(propData.images) && propData.images.length
            ? propData.images
            : (Array.isArray(expProperty.images) && expProperty.images.length
              ? expProperty.images
              : (Array.isArray(explorerData.images) ? explorerData.images : []));

          setFullDetails({
            ...explorerData,
            property: mergedProperty,
            documents: documents.length > 0 ? documents : (explorerData.documents || []),
            images: images
          });
        })
        .catch((requestError) => {
          setFullDetails(null);
          setDetailsError(formatApiError(requestError) || "Property details could not be loaded.");
        })
        .finally(() => {
          setLoadingDetails(false);
        });
    } else {
      setFullDetails(null);
      setDetailsError("");
    }
  }, [selectedId, activeTab, detailsReloadKey]);

  // Load independent approval types together so the tab badges represent one
  // consistent snapshot, including the new protected identity-review queue.
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [p, a, pl, ov] = await Promise.allSettled([
        api.get("/admin-bff/pending-properties"),
        api.get("/admin/pending-agents"),
        api.get("/admin/plot-demands"),
        api.get("/admin/owner-verifications"),
      ]);
      setProperties(p.status === "fulfilled" ? asItems(p.value.data) : []);
      setAgents(a.status === "fulfilled" ? asItems(a.value.data) : []);
      setPlots(pl.status === "fulfilled" ? asItems(pl.value.data) : []);
      setOwnerVerifications(ov.status === "fulfilled" ? asItems(ov.value.data) : []);
    } catch (err) {
      setLoadError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Filter queues
  const propertiesQueue = useMemo(() =>
    properties.filter((p) =>
      ["PENDING_FINAL_APPROVAL", "PENDING_VERIFICATION", "PENDING_SOLD_VERIFICATION", "IN_PROCESS", "NEW", "SUBMITTED", "VERIFIED"].includes(
        String(p.status || "").toUpperCase()
      )
    ),
    [properties]
  );

  const plotsQueue = useMemo(() =>
    plots.filter((p) =>
      !["COMPLETED", "CANCELLED"].includes(String(p.status || "").toUpperCase())
    ),
    [plots]
  );

  const agentsQueue = useMemo(() =>
    agents.filter((a) =>
      String(a.role || "").toLowerCase() === "agent" &&
      String(a.status || "").toUpperCase() === "PENDING"
    ),
    [agents]
  );

  const officesQueue = useMemo(() =>
    agents.filter((a) =>
      (String(a.role || "").toLowerCase() === "office" ||
       String(a.role || "").toLowerCase() === "office_user") &&
      String(a.status || "").toUpperCase() === "PENDING"
    ),
    [agents]
  );

  // Completed/rejected identity records remain audit history on the backend and
  // are intentionally excluded from this action queue.
  const ownersQueue = useMemo(() =>
    ownerVerifications.filter((item) => String(item.status || "").toLowerCase() === "pending_admin_review"),
    [ownerVerifications]
  );

  // Get active queue based on tab
  const activeQueue = useMemo(() => {
    switch (activeTab) {
      case "properties":
        return propertiesQueue;
      case "plots":
        return plotsQueue;
      case "agents":
        return agentsQueue;
      case "offices":
        return officesQueue;
      case "owners":
        return ownersQueue;
      default:
        return [];
    }
  }, [activeTab, propertiesQueue, plotsQueue, agentsQueue, officesQueue, ownersQueue]);

  const uniqueCategories = useMemo(() => {
    const cats = propertiesQueue.map(item => item.category).filter(Boolean);
    return Array.from(new Set(cats));
  }, [propertiesQueue]);

  const uniquePropertyStatuses = useMemo(() => {
    const stats = propertiesQueue.map(item => item.status).filter(Boolean);
    return Array.from(new Set(stats));
  }, [propertiesQueue]);

  const uniquePlotStatuses = useMemo(() => {
    const stats = plotsQueue.map(item => item.status).filter(Boolean);
    return Array.from(new Set(stats));
  }, [plotsQueue]);

  const uniqueUserCities = useMemo(() => {
    const list = activeTab === "agents" ? agentsQueue : officesQueue;
    const cities = list.map(item => item.city).filter(Boolean);
    return Array.from(new Set(cities));
  }, [agentsQueue, officesQueue, activeTab]);

  const uniqueUserStatuses = useMemo(() => {
    const list = activeTab === "agents" ? agentsQueue : officesQueue;
    const stats = list.map(item => item.status).filter(Boolean);
    return Array.from(new Set(stats));
  }, [agentsQueue, officesQueue, activeTab]);

  // Apply search query and dropdown filters
  const searchFilteredQueue = useMemo(() => {
    let list = activeQueue;

    // Apply activeTab-specific filters
    if (activeTab === "properties") {
      if (filterCategory !== "all") {
        list = list.filter(item => String(item.category || "").toLowerCase() === filterCategory.toLowerCase());
      }
      if (filterStatus !== "all") {
        list = list.filter(item => String(item.status || "").toLowerCase() === filterStatus.toLowerCase());
      }
    } else if (activeTab === "plots") {
      if (filterStatus !== "all") {
        list = list.filter(item => String(item.status || "").toLowerCase() === filterStatus.toLowerCase());
      }
    } else if (activeTab === "agents" || activeTab === "offices") {
      if (filterStatus !== "all") {
        list = list.filter(item => String(item.status || "").toLowerCase() === filterStatus.toLowerCase());
      }
      if (filterCity !== "all") {
        list = list.filter(item => String(item.city || "").toLowerCase() === filterCity.toLowerCase());
      }
    }

    // Apply search query
    const query = searchQuery.trim().toLowerCase();
    if (!query) return list;
    return list.filter((item) => {
      const title = String(item.title || item.property_name || item.name || "").toLowerCase();
      const id = String(item.property_id || item.user_id || item.id || "").toLowerCase();
      const sub = String(item.locality || item.city || item.role || "").toLowerCase();
      const location = String(item.location || "").toLowerCase();
      const purpose = String(item.purpose || "").toLowerCase();
      return (
        title.includes(query) ||
        id.includes(query) ||
        sub.includes(query) ||
        location.includes(query) ||
        purpose.includes(query)
      );
    });
  }, [activeQueue, searchQuery, filterCategory, filterStatus, filterCity, activeTab]);

  // Reset filters when active tab changes
  useEffect(() => {
    setFilterCategory("all");
    setFilterStatus("all");
    setFilterCity("all");
    setFiltersOpen(false);
  }, [activeTab]);

  // Set default selected item when active tab or queues change
  useEffect(() => {
    if (searchFilteredQueue.length > 0) {
      const firstItem = searchFilteredQueue[0];
      setSelectedId(firstItem.property_id || firstItem.user_id || firstItem.id);
    } else {
      setSelectedId(null);
    }
    setComments("");
  }, [activeTab, searchFilteredQueue]);
  
  const selectedItem = useMemo(() => {
    return activeQueue.find(
      (item) => (item.property_id || item.user_id || item.id) === selectedId
    );
  }, [activeQueue, selectedId]);

  const isSoldReviewSelected = activeTab === "properties" && isPendingSoldStatus(selectedItem?.status);

  const handleApprove = async () => {
    if (!selectedItem) return;
    setBusy(true);
    const id = selectedItem.property_id || selectedItem.user_id || selectedItem.id;
    try {
      if (activeTab === "properties") {
        // Purpose: sold verification has its own admin endpoint so status history and sold_at are correct.
        if (isPendingSoldStatus(selectedItem.status)) {
          await approveSoldVerification(id, comments.trim() || "Admin verified property as sold");
          toast.success("Sold request approved. Property is now SOLD.");
        } else {
          await api.post(`/admin/properties/${id}/approve`);
          toast.success("Approved property successfully");
        }
      } else if (activeTab === "plots") {
        // Plots on Demand is a fulfilment workflow, not a listing approval.
        // This legacy queue action now moves it into matching using the same
        // validated lifecycle as the dedicated workbench.
        await api.put(`/admin/plot-demands/${id}/status`, { status: "MATCHING", note: comments.trim() });
        toast.success("Plots on Demand request moved to matching");
      } else if (activeTab === "owners") {
        // The backend re-checks that these are still the user's current files
        // and that at least one contact method is still verified.
        await api.post(`/admin/owner-verifications/${id}/approve`, { remarks: comments.trim() });
        toast.success("Identity verification approved");
      } else {
        await api.post(`/admin/users/${id}/approve`);
        toast.success("Approved user credentials successfully");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (!selectedItem || !comments.trim()) return;
    setBusy(true);
    const id = selectedItem.property_id || selectedItem.user_id || selectedItem.id;
    try {
      if (activeTab === "properties") {
        // Purpose: rejecting sold verification restores previous active status, not REJECTED listing.
        if (isPendingSoldStatus(selectedItem.status)) {
          await rejectSoldVerification(id, comments.trim(), soldReviewFor(selectedItem).previous_status);
          toast.success("Sold request rejected and property restored");
        } else {
          await api.post(`/admin/properties/${id}/reject`, { reason: comments.trim() });
          toast.success("Rejected property with comment reason");
        }
      } else if (activeTab === "plots") {
        await api.put(`/admin/plot-demands/${id}/status`, { status: "CANCELLED", note: comments.trim() });
        toast.success("Plots on Demand request cancelled");
      } else if (activeTab === "owners") {
        await api.post(`/admin/owner-verifications/${id}/reject`, { remarks: comments.trim() });
        toast.success("Identity verification rejected");
      } else {
        // Agent/office rejection is a real audited account block. Previously
        // this branch displayed success without performing a backend action.
        await api.delete(`/admin/users/${id}`, { data: { reason: comments.trim() } });
        toast.success("User credentials rejected and account blocked");
      }
      setComments("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const handleSendBack = async () => {
    if (!selectedItem || !comments.trim()) return;
    setBusy(true);
    const id = selectedItem.property_id || selectedItem.user_id || selectedItem.id;
    try {
      await api.post(`/admin/properties/${id}/send-back`, { reason: comments.trim() });
      toast.success("Sent property back to office with comments");
      setComments("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const selectedPublicPath = !selectedItem ? "" : activeTab === "properties"
    ? (String(selectedItem.status || "").toUpperCase() === "PUBLISHED" ? `/properties/${selectedItem.property_id || selectedItem.id}` : "")
    : activeTab === "agents"
      ? (String(selectedItem.status || "").toUpperCase() === "ACTIVE" && selectedItem.verified_owner ? `/agents/${selectedItem.user_id || selectedItem.id}` : "")
      : activeTab === "offices"
        ? (selectedItem.office_id ? `/offices/${selectedItem.office_id}` : "")
        : "";

  return (
    <div className="bg-background text-foreground min-h-screen" data-testid="admin-approvals-page">
      {/* Header */}
      <div className="flex flex-col w-full mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">Admin Control Plane</p>
        <h1 className="font-display text-4xl font-black tracking-tighter text-foreground">Final <span className="brand-gradient-text">Verification</span></h1>
      </div>

      {loadError && <div className="mb-6"><WorkbenchError message={loadError} onRetry={load} /></div>}

      {/* Tabs */}
      <div className="flex gap-6 border-b border-border mb-6 overflow-x-auto scrollbar-none">
        {["properties", "plots", "owners", "agents", "offices"].map((tab) => (
          <button
            type="button"
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setSearchQuery("");
            }}
            className={`pb-3 text-sm font-semibold capitalize transition-all border-b-2 ${
              activeTab === tab
                ? "text-primary border-primary font-bold"
                : "text-muted-foreground border-transparent hover:text-foreground"
            }`}
          >
            {tab === "plots" ? "Plots on Demand" : tab} ({
              tab === "properties" ? propertiesQueue.length :
              tab === "plots" ? plotsQueue.length :
              tab === "owners" ? ownersQueue.length :
              tab === "agents" ? agentsQueue.length : officesQueue.length
            })
          </button>
        ))}
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col md:flex-row gap-4 mb-6 items-center justify-between w-full max-w-full">
        <div className="flex gap-3 flex-grow w-full md:w-auto max-w-full">
          <div className="relative flex-grow min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-muted/30 border border-border rounded-lg pl-10 pr-4 py-2.5 text-foreground placeholder-muted-foreground/50 transition-all outline-none focus:border-primary/50"
              placeholder={activeTab === "plots" ? "Search plots on demand..." : `Search ${activeTab}...`}
              type="text"
            />
          </div>
          {activeTab !== "owners" && (
            <button
              type="button"
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`px-4 py-2.5 rounded-lg border transition-colors flex items-center gap-2 text-sm font-semibold ${
                filtersOpen
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-muted/30 border-border text-foreground hover:bg-muted/50"
              }`}
            >
              <Filter className="w-4 h-4" />
              <span>Filter</span>
            </button>
          )}
        </div>
      </div>

      {/* Toggleable Filter Panel */}
      {filtersOpen && (
        <div className="bg-card border border-border rounded-xl p-4 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4 animate-fade-up w-full max-w-full box-border overflow-hidden">
          {activeTab === "properties" && (
            <>
              <div>
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">Category</label>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="w-full bg-background border border-border rounded-lg px-3 py-1.5 text-xs text-foreground focus:border-primary/50 outline-none capitalize"
                >
                  <option value="all">All Categories</option>
                  {uniqueCategories.map(cat => (
                    <option key={cat} value={cat}>{cat.toLowerCase()}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full bg-background border border-border rounded-lg px-3 py-1.5 text-xs text-foreground focus:border-primary/50 outline-none capitalize"
                >
                  <option value="all">All Statuses</option>
                  {uniquePropertyStatuses.map(stat => (
                    <option key={stat} value={stat}>{stat.toLowerCase().replace(/_/g, " ")}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {activeTab === "plots" && (
            <div>
              <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-3 py-1.5 text-xs text-foreground focus:border-primary/50 outline-none capitalize"
              >
                <option value="all">All Statuses</option>
                {uniquePlotStatuses.map(stat => (
                  <option key={stat} value={stat}>{stat.toLowerCase().replace(/_/g, " ")}</option>
                ))}
              </select>
            </div>
          )}

          {(activeTab === "agents" || activeTab === "offices") && (
            <>
              <div>
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">City</label>
                <select
                  value={filterCity}
                  onChange={(e) => setFilterCity(e.target.value)}
                  className="w-full bg-background border border-border rounded-lg px-3 py-1.5 text-xs text-foreground focus:border-primary/50 outline-none"
                >
                  <option value="all">All Cities</option>
                  {uniqueUserCities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full bg-background border border-border rounded-lg px-3 py-1.5 text-xs text-foreground focus:border-primary/50 outline-none capitalize"
                >
                  <option value="all">All Statuses</option>
                  {uniqueUserStatuses.map(stat => (
                    <option key={stat} value={stat}>{stat.toLowerCase().replace(/_/g, " ")}</option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>
      )}

      {/* Split Pane Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Left Column: Queue List */}
        <div className={`xl:col-span-4 flex flex-col gap-4 xl:h-[calc(100vh-210px)] xl:overflow-y-auto pr-2 pb-4 scrollbar-none ${selectedItem ? "hidden xl:flex" : "flex"}`}>
          {loading ? (
            <WorkbenchLoading />
          ) : searchFilteredQueue.length === 0 ? (
            <Empty msg={`No pending ${activeTab}`} />
          ) : (
            searchFilteredQueue.map((item) => {
              const id = item.property_id || item.user_id || item.id;
              const isSelected = id === selectedId;
              
              let title = item.title || item.property_name || item.name;
              let sub = item.locality || item.city || item.role || "—";
              let details = item.price ? formatPrice(item.price) : (item.email || "—");
              
              if (activeTab === "plots") {
                const areaNumber = Number(item.area);
                const areaText = Number.isFinite(areaNumber)
                  ? `${areaNumber.toLocaleString("en-IN")}${item.area_unit ? ` ${String(item.area_unit).replace(/_/g, " ")}` : ""}`
                  : "Area unavailable";
                title = `Plots on Demand (${areaText})`;
                sub = item.location || "—";
                details = item.budget ? formatPrice(item.budget) : "—";
              }
              
              if (!title) title = "Untitled";
              const badge = item.status || "PENDING";
              const imgUrl = item.images?.[0] || item.thumbnail_url || (activeTab === "owners" ? item.selfie_url : "");
              const safeImgUrl = getImageUrl(imgUrl);
              return (
                <button
                  type="button"
                  key={id}
                  aria-pressed={isSelected}
                  onClick={() => setSelectedId(id)}
                  className={`w-full p-4 text-left rounded-xl border cursor-pointer relative overflow-hidden transition-all duration-200 ease-out group shrink-0 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                    isSelected
                      ? "bg-muted border-primary/50 shadow-md"
                      : "bg-card border-border hover:border-border-hover"
                  }`}
                >
                  {isSelected && <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary"></div>}
                  <div className="flex gap-4">
                    <div className="w-16 h-16 rounded bg-background overflow-hidden flex-shrink-0 border border-border">
                      {safeImgUrl ? (
                        <img src={safeImgUrl} alt={title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground">No image</div>
                      )}
                    </div>
                    <div className="flex flex-col justify-between flex-grow min-w-0">
                      <div>
                        <div className="flex justify-between items-start gap-2">
                          <span className="font-mono text-[10px] text-primary truncate">ID: {String(id).slice(-6).toUpperCase()}</span>
                          <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-primary/10 text-primary border border-primary/20 shrink-0">{badge}</span>
                        </div>
                        <h3 className="font-semibold text-sm leading-tight mt-1 text-foreground truncate">{title}</h3>
                        <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                          <MapPin className="w-3 h-3 inline mr-1 text-muted-foreground" />
                          {sub}
                        </p>
                      </div>
                      <div className="font-mono text-xs text-foreground font-bold mt-2">{details}</div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Right Column: Detailed View */}
        <div className={`xl:col-span-8 bg-card border border-border rounded-xl flex-col xl:h-[calc(100vh-210px)] xl:overflow-y-auto ${selectedItem ? "flex" : "hidden xl:flex"}`}>
          {selectedItem ? (
            <>
              {/* Mobile Back Button */}
              <div className="p-3 border-b border-border xl:hidden flex items-center shrink-0">
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  className="text-xs font-bold text-primary flex items-center gap-1 hover:underline"
                >
                  <ArrowLeft className="w-4 h-4 mr-1 inline" />
                  <span>Back to List</span>
                </button>
              </div>

              {/* Detail Header */}
              <div className="relative h-48 w-full bg-background border-b border-border flex-shrink-0">
                {loadingDetails && (
                  <div className="absolute inset-0 bg-background/60 flex items-center justify-center z-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                )}
                <div className="absolute inset-0 z-0 bg-slate-900 overflow-hidden">
                  {(fullDetails?.property?.images?.[0]?.url || selectedItem.images?.[0] || selectedItem.thumbnail_url) ? (
                    <img
                      src={getImageUrl(fullDetails?.property?.images?.[0]?.url || selectedItem.images?.[0] || selectedItem.thumbnail_url)}
                      alt="Cover"
                      className="w-full h-full object-cover opacity-40 mix-blend-luminosity"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-muted/50 to-background flex items-center justify-center text-muted-foreground/30">
                      <Building2 className="w-12 h-12 text-muted-foreground/30" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent"></div>
                </div>
                <div className="absolute bottom-0 left-0 p-6 z-10 w-full flex justify-between items-end gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded bg-background border border-primary/50 text-primary font-mono text-[10px] uppercase tracking-wider backdrop-blur-sm">
                        ID: {String(selectedItem.property_id || selectedItem.user_id || selectedItem.id).toUpperCase()}
                      </span>
                      <span className="px-2 py-0.5 rounded bg-muted/80 border border-border text-foreground text-[10px] capitalize backdrop-blur-sm">
                        {selectedItem.category || selectedItem.role || "Listing"}
                      </span>
                    </div>
                    <h2 className="font-display text-2xl font-bold text-foreground leading-none truncate">
                      {activeTab === "plots"
                        ? `Plots on Demand (${formatAreaValue(selectedItem.area, selectedItem.area_unit)})`
                        : (selectedItem.title || selectedItem.property_name || selectedItem.name || "Untitled")
                      }
                    </h2>
                    {selectedPublicPath && (
                      <Link
                        to={selectedPublicPath}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-primary hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" /> View public page
                      </Link>
                    )}
                    {!selectedPublicPath && ["properties", "agents", "offices"].includes(activeTab) && (
                      <p className="mt-1.5 text-[11px] font-semibold text-muted-foreground">Public page becomes available after approval.</p>
                    )}
                  </div>
                  {(selectedItem.price || selectedItem.budget) && (
                    <div className="text-right backdrop-blur-md bg-background/50 p-3 rounded-lg border border-border shrink-0">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-0.5">Listed Valuation</p>
                      <p className="font-mono text-lg text-primary font-bold">
                        {formatPrice(selectedItem.price || selectedItem.budget)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Detail Body */}
              <div className="p-6 space-y-8">
                {detailsError && <WorkbenchError message={detailsError} onRetry={() => setDetailsReloadKey((value) => value + 1)} />}
                {activeTab === "properties" && (() => {
                  // The protected explorer returns property, documents, history,
                  // leads and visits as sibling projections. Merge the listing
                  // fields with the queue snapshot without losing those related
                  // collections when details finish loading.
                  const prop = { ...selectedItem, ...(fullDetails?.property || {}) };
                  const land = prop.land_details || {};
                  const bldg = prop.building_details || {};
                  const amenities = prop.amenities || [];
                  const images = (Array.isArray(fullDetails?.images) && fullDetails.images.length)
                    ? fullDetails.images
                    : ((Array.isArray(prop.images) && prop.images.length)
                      ? prop.images
                      : [prop.thumbnail_url].filter(Boolean));
                  const documents = (Array.isArray(fullDetails?.documents) && fullDetails.documents.length)
                    ? fullDetails.documents
                    : (prop.documents || []);
                  return (
                    <div className="space-y-6 text-xs">
                      {/* Images gallery */}
                      {images.length > 0 && (
                        <div>
                          <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 border-b border-border/30 pb-1.5">Property Images</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {images.map((img, idx) => {
                              const url = typeof img === "string" ? img : (img?.url || img?.file_url || img?.public_url || "");
                              const safeUrl = getImageUrl(url);
                              const name = typeof img === "string" ? img.split("/").pop() : (img?.file_name || "Property");
                              const id = typeof img === "string" ? idx : (img?.id || idx);
                              if (!safeUrl) return null;
                              return (
                                <button
                                  type="button"
                                  key={id}
                                  onClick={() => setActiveImage(safeUrl)}
                                  aria-label={`Open ${name} image preview`}
                                  className="relative aspect-video rounded-lg overflow-hidden border border-border bg-muted cursor-pointer hover:border-primary/50 hover:opacity-90 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                >
                                  <img src={safeUrl} alt={name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Documents */}
                      {documents.length > 0 && (
                        <div>
                          <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 border-b border-border/30 pb-1.5">Verification Documents</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {documents.map((doc) => {
                              const docUrl = doc.url || doc.file_url || doc.public_url;
                              return <div key={doc.id || doc.document_type} className="p-3 rounded-lg border border-border bg-card flex justify-between items-center">
                                <div>
                                  <p className="font-bold text-foreground capitalize">{String(doc.document_type || "document").replace(/_/g, " ")}</p>
                                  <p className="text-[10px] text-muted-foreground mt-0.5">{doc.file_name || "Document File"}</p>
                                </div>
                                {docUrl ? <button type="button" onClick={() => handlePreviewDocument(docUrl, doc.id || doc.document_type, String(doc.document_type || "document").replace(/_/g, " "))} disabled={loadingDocId === (doc.id || doc.document_type)} className="px-2.5 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20 text-[10px] font-semibold transition-all cursor-pointer disabled:opacity-50">{loadingDocId === (doc.id || doc.document_type) ? "Loading..." : "View Doc"}</button> : <span className="text-[10px] font-semibold text-muted-foreground">Unavailable</span>}
                              </div>;
                            })}
                          </div>
                        </div>
                      )}

                      {isPendingSoldStatus(prop.status) && <SoldReviewPanel property={prop} fallback={selectedItem} />}

                      {/* Detailed parameters */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-6">
                          <div>
                            <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 border-b border-border/30 pb-1.5">General Info</h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-muted-foreground mb-0.5">Title</p>
                                <p className="font-semibold text-foreground">{prop.title || "—"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Property Code</p>
                                <p className="font-mono font-semibold text-foreground">{prop.property_code || "—"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Category</p>
                                <p className="font-semibold text-foreground uppercase">{prop.category || "—"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Listing Type</p>
                                <p className="font-semibold text-foreground uppercase">{prop.listing_type || "—"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Price</p>
                                <p className="font-semibold text-primary">{prop.price ? formatPrice(prop.price) : "—"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Total Area</p>
                                <p className="font-mono text-foreground">{formatAreaValue(prop.area, prop.area_unit)}</p>
                              </div>
                              <div className="col-span-2">
                                <p className="text-muted-foreground mb-0.5">Description</p>
                                <p className="text-foreground leading-relaxed whitespace-pre-wrap">{prop.description || "—"}</p>
                              </div>
                            </div>
                          </div>

                          <div>
                            <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 border-b border-border/30 pb-1.5">Location details</h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-muted-foreground mb-0.5">Locality</p>
                                <p className="font-semibold text-foreground">{prop.locality || "—"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Sector</p>
                                <p className="font-semibold text-foreground">{prop.sector || "—"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">City</p>
                                <p className="font-semibold text-foreground">{prop.city || "—"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">State</p>
                                <p className="font-semibold text-foreground">{prop.state || "—"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Pincode</p>
                                <p className="font-mono text-foreground">{prop.pincode || "—"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Country</p>
                                <p className="font-semibold text-foreground">{prop.country || "—"}</p>
                              </div>
                              <div className="col-span-2">
                                <p className="text-muted-foreground mb-0.5">Full Address</p>
                                <p className="text-foreground">{prop.address || "—"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Latitude</p>
                                <p className="font-mono text-foreground">{prop.latitude || "—"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Longitude</p>
                                <p className="font-mono text-foreground">{prop.longitude || "—"}</p>
                              </div>
                              {prop.latitude && prop.longitude && (
                                <div className="col-span-2 mt-4">
                                  <p className="text-muted-foreground mb-2">Location Map (OpenStreetMap)</p>
                                  <div className="relative w-full h-[200px] rounded-lg overflow-hidden border border-border bg-muted">
                                    <iframe
                                      width="100%"
                                      height="100%"
                                      frameBorder="0"
                                      scrolling="no"
                                      marginHeight="0"
                                      marginWidth="0"
                                      title="Property Location Map"
                                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(prop.longitude)-0.015}%2C${parseFloat(prop.latitude)-0.01}%2C${parseFloat(prop.longitude)+0.015}%2C${parseFloat(prop.latitude)+0.01}&layer=mapnik&marker=${parseFloat(prop.latitude)}%2C${parseFloat(prop.longitude)}`}
                                      className="absolute inset-0"
                                    ></iframe>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-6">
                          <div>
                            <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 border-b border-border/30 pb-1.5">Land Details</h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-muted-foreground mb-0.5">Facing</p>
                                <p className="font-semibold text-foreground capitalize">{land.facing || "—"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Road Width</p>
                                <p className="font-semibold text-foreground">{land.road_width_ft ? `${land.road_width_ft} ft` : "—"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Corner Plot</p>
                                <p className="font-semibold text-foreground">{land.corner_plot ? "Yes" : "No"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Registry Available</p>
                                <p className="font-semibold text-foreground">{land.registry_available ? "Yes" : "No"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Mutation Available</p>
                                <p className="font-semibold text-foreground">{land.mutation_available ? "Yes" : "No"}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Khatiyan</p>
                                <p className="font-semibold text-foreground">{land.khatiyan || "—"}</p>
                              </div>
                              <div className="col-span-2">
                                <p className="text-muted-foreground mb-0.5">Jamabandi</p>
                                <p className="font-semibold text-foreground">{land.jamabandi || "—"}</p>
                              </div>
                            </div>
                          </div>

                          {prop.category !== "LAND" && prop.category !== "PLOT" && (
                            <div>
                              <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 border-b border-border/30 pb-1.5">Building Details</h4>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-muted-foreground mb-0.5">Construction Year</p>
                                  <p className="font-semibold text-foreground">{bldg.construction_year || "—"}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground mb-0.5">Total Floors</p>
                                  <p className="font-semibold text-foreground">{bldg.total_floors || "—"}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground mb-0.5">Floor No</p>
                                  <p className="font-semibold text-foreground">{bldg.floor_no || "—"}</p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground mb-0.5">Bedrooms / Bathrooms</p>
                                  <p className="font-semibold text-foreground">{bldg.bedrooms || 0} Bed, {bldg.bathrooms || 0} Bath</p>
                                </div>
                                <div className="col-span-2">
                                  <p className="text-muted-foreground mb-0.5">Furnished Status</p>
                                  <p className="font-semibold text-foreground capitalize">{bldg.furnished_status || "—"}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {amenities.length > 0 && (
                            <div>
                              <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 border-b border-border/30 pb-1.5">Amenities</h4>
                              <div className="flex flex-wrap gap-2">
                                {amenities.map((amenity, idx) => (
                                  <span key={idx} className="px-2.5 py-1 rounded bg-muted text-foreground text-[10px] font-semibold border border-border">
                                    {amenity}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          <div>
                            <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 border-b border-border/30 pb-1.5">System Stats</h4>
                            <div className="grid grid-cols-2 gap-4 font-mono text-[10px]">
                              <div>
                                <p className="text-muted-foreground mb-0.5">Views</p>
                                <p className="text-foreground">{prop.view_count || 0}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Contacts</p>
                                <p className="text-foreground">{prop.contact_count || 0}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Site Visits</p>
                                <p className="text-foreground">{prop.site_visit_count || 0}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Phone Reveals</p>
                                <p className="text-foreground">{prop.phone_reveal_count || 0}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">WhatsApp Clicks</p>
                                <p className="text-foreground">{prop.whatsapp_click_count || 0}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground mb-0.5">Verified Documents</p>
                                <p className={prop.verified_documents ? "text-emerald-400 font-bold" : "text-amber-400"}>
                                  {prop.verified_documents ? "VERIFIED" : "PENDING"}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {activeTab === "plots" && (
                  <div>
                    <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 border-b border-border/30 pb-1.5">Plots on Demand Parameters</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6 text-xs">
                      <div>
                        <p className="text-muted-foreground mb-0.5">Location</p>
                        <p className="font-semibold text-foreground">{selectedItem.location || "—"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-0.5">Budget</p>
                        <p className="font-semibold text-primary">{selectedItem.budget ? formatPrice(selectedItem.budget) : "—"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-0.5">Total Area</p>
                        <p className="font-mono font-semibold text-foreground">
                          {formatAreaValue(selectedItem.area, selectedItem.area_unit)}
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-0.5">Purpose</p>
                        <p className="font-semibold text-foreground capitalize">{selectedItem.purpose || "—"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-0.5">Timeframe</p>
                        <p className="font-semibold text-foreground">{selectedItem.timeframe || "—"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-0.5">Facing Preference</p>
                        <p className="font-semibold text-foreground capitalize">{selectedItem.facing_preference || "—"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-0.5">Road Width Preference</p>
                        <p className="font-semibold text-foreground">{selectedItem.preferred_road_width || "—"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-0.5">Contact Phone</p>
                        <p className="font-semibold text-foreground">{selectedItem.contact_phone || "—"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-0.5">Status</p>
                        <p className="font-semibold text-foreground uppercase">{selectedItem.status || "—"}</p>
                      </div>
                      {selectedItem.assigned_agent_id && (
                        <div>
                          <p className="text-muted-foreground mb-0.5">Assigned Agent UUID</p>
                          <p className="font-mono text-foreground">{selectedItem.assigned_agent_id}</p>
                        </div>
                      )}
                      {selectedItem.assigned_office_id && (
                        <div>
                          <p className="text-muted-foreground mb-0.5">Assigned Office UUID</p>
                          <p className="font-mono text-foreground">{selectedItem.assigned_office_id}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-muted-foreground mb-0.5">Created At</p>
                        <p className="font-semibold text-foreground">{new Date(selectedItem.created_at).toLocaleString("en-IN")}</p>
                      </div>
                      <div className="col-span-2 md:col-span-3">
                        <p className="text-muted-foreground mb-0.5">Buyer Note</p>
                        <p className="text-foreground leading-relaxed whitespace-pre-wrap">{selectedItem.buyer_note || "—"}</p>
                      </div>
                    </div>
                  </div>
                )}

                {(activeTab === "agents" || activeTab === "offices") && (
                  <div>
                    <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 border-b border-border/30 pb-1.5">User Account Details</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6 text-xs mb-8">
                      <div>
                        <p className="text-muted-foreground mb-0.5">Name</p>
                        <p className="font-semibold text-foreground">{selectedItem.name || "—"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-0.5">Email</p>
                        <p className="font-semibold text-foreground truncate">{selectedItem.email || "—"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-0.5">Phone</p>
                        <p className="font-semibold text-foreground">{selectedItem.phone || "—"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-0.5">Role</p>
                        <p className="font-semibold text-foreground uppercase">{selectedItem.role || "—"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-0.5">Status</p>
                        <p className="font-semibold text-foreground uppercase">{selectedItem.status || "—"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-0.5">Created At</p>
                        <p className="font-semibold text-foreground">{new Date(selectedItem.created_at).toLocaleString("en-IN")}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-0.5">Phone Verified</p>
                        <p className="font-semibold text-foreground">{selectedItem.phone_verified ? "Yes" : "No"}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-0.5">Owner Verified</p>
                        <p className="font-semibold text-foreground">{selectedItem.verified_owner ? "Yes" : "No"}</p>
                      </div>
                    </div>

                    {selectedItem.raw && Object.keys(selectedItem.raw).length > 0 && (
                      <div>
                        <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 border-b border-border/30 pb-1.5">Profile Metadata</h4>
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          {selectedItem.raw.office_id && (
                            <div>
                              <p className="text-muted-foreground mb-0.5">Associated Office UUID</p>
                              <p className="font-mono text-foreground">{selectedItem.raw.office_id}</p>
                            </div>
                          )}
                          {selectedItem.raw.experience_years && (
                            <div>
                              <p className="text-muted-foreground mb-0.5">Experience</p>
                              <p className="font-semibold text-foreground">{selectedItem.raw.experience_years} Years</p>
                            </div>
                          )}
                          {selectedItem.raw.about && (
                            <div className="col-span-2">
                              <p className="text-muted-foreground mb-0.5">About / Bio</p>
                              <p className="text-foreground leading-relaxed whitespace-pre-wrap">{selectedItem.raw.about}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "owners" && (
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 border-b border-border/30 pb-1.5">Identity Review</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <IdentityReviewAsset url={selectedItem.selfie_url} label="Selfie" />
                        <IdentityReviewAsset url={selectedItem.id_proof_url} label="Government ID" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-5 text-xs">
                      <div><p className="text-muted-foreground">Name</p><p className="mt-1 font-semibold text-foreground">{selectedItem.name || "—"}</p></div>
                      <div><p className="text-muted-foreground">Role</p><p className="mt-1 font-semibold uppercase text-foreground">{selectedItem.role || "—"}</p></div>
                      <div><p className="text-muted-foreground">Phone verified</p><p className="mt-1 font-semibold text-foreground">{selectedItem.phone_verified ? "Yes" : "No"}</p></div>
                      <div><p className="text-muted-foreground">Email verified</p><p className="mt-1 font-semibold text-foreground">{selectedItem.email_verified ? "Yes" : "No"}</p></div>
                      <div><p className="text-muted-foreground">Submitted</p><p className="mt-1 font-semibold text-foreground">{new Date(selectedItem.created_at).toLocaleString("en-IN")}</p></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Action Panel */}
              <div className="border-t border-border bg-background/50 p-6 flex-shrink-0">
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest block mb-2" htmlFor="admin-comments">
                  {isSoldReviewSelected ? "Sold Verification Comments" : activeTab === "owners" ? "Identity Review Notes" : activeTab === "plots" ? "Plots on Demand Workflow Note" : "Review Comments (Required for Rejection)"}
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  className="w-full bg-card border border-border rounded-lg p-3 text-foreground text-sm focus:border-primary/50 transition-all outline-none mb-4 placeholder-muted-foreground/30"
                  id="admin-comments"
                  placeholder={isSoldReviewSelected ? "Add approve/reject remarks for sold verification..." : activeTab === "owners" ? "Add review notes; a reason is required when rejecting..." : activeTab === "plots" ? "Add a note before starting matching or cancelling this request..." : "Add the rejection reason or internal review note..."}
                  rows="2"
                />
                 <div className="flex flex-wrap gap-4">
                  {activeTab === "properties" ? (
                    isSoldReviewSelected ? (
                      <>
                        <button
                          type="button"
                          onClick={handleReject}
                          disabled={busy || !comments.trim()}
                          className="px-5 py-2.5 rounded-lg border border-border text-muted-foreground hover:text-destructive hover:bg-destructive/10 font-bold transition-all flex items-center gap-2 text-sm disabled:opacity-50"
                        >
                          Reject Sold Request
                        </button>
                        <button
                          type="button"
                          onClick={handleApprove}
                          disabled={busy}
                          className="px-8 py-2.5 rounded-lg bg-primary text-primary-foreground font-bold hover:shadow-[0_0_15px_rgba(173,198,255,0.4)] transition-all flex items-center gap-2 text-sm ml-auto"
                        >
                          Approve Sold
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={handleReject}
                          disabled={busy || !comments.trim()}
                          className="px-5 py-2.5 rounded-lg border border-border text-muted-foreground hover:text-destructive hover:bg-destructive/10 font-bold transition-all flex items-center gap-2 text-sm disabled:opacity-50"
                        >
                          Reject Listing
                        </button>
                        <button
                          type="button"
                          onClick={handleSendBack}
                          disabled={busy || !comments.trim()}
                          className="px-5 py-2.5 rounded-lg border border-border text-muted-foreground hover:text-amber-500 hover:bg-amber-500/10 font-bold transition-all flex items-center gap-2 text-sm disabled:opacity-50"
                        >
                          Send Back to Office
                        </button>
                        <button
                          type="button"
                          onClick={handleApprove}
                          disabled={busy}
                          className="px-8 py-2.5 rounded-lg bg-primary text-primary-foreground font-bold hover:shadow-[0_0_15px_rgba(173,198,255,0.4)] transition-all flex items-center gap-2 text-sm ml-auto"
                        >
                          Approve & Publish
                        </button>
                      </>
                    )
                  ) : activeTab === "plots" ? (
                    <>
                      <button
                        type="button"
                        onClick={handleReject}
                        disabled={busy || !comments.trim()}
                        className="px-5 py-2.5 rounded-lg border border-border text-muted-foreground hover:text-destructive hover:bg-destructive/10 font-bold transition-all flex items-center gap-2 text-sm disabled:opacity-50"
                      >
                        Cancel Request
                      </button>
                      {String(selectedItem.status || "NEW").toUpperCase() === "NEW" ? (
                        <button
                          type="button"
                          onClick={handleApprove}
                          disabled={busy}
                          className="px-8 py-2.5 rounded-lg bg-primary text-primary-foreground font-bold hover:shadow-[0_0_15px_rgba(173,198,255,0.4)] transition-all flex items-center gap-2 text-sm ml-auto"
                        >
                          Start Matching
                        </button>
                      ) : (
                        <Link
                          to={`/dashboard/admin/plots-on-demand/workbench?select=${encodeURIComponent(selectedItem.id)}`}
                          className="px-8 py-2.5 rounded-lg bg-primary text-primary-foreground font-bold hover:shadow-[0_0_15px_rgba(173,198,255,0.4)] transition-all flex items-center gap-2 text-sm ml-auto"
                        >
                          Continue in Workbench
                        </Link>
                      )}
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={handleReject}
                        disabled={busy || !comments.trim()}
                        className="px-5 py-2.5 rounded-lg border border-border text-muted-foreground hover:text-destructive hover:bg-destructive/10 font-bold transition-all flex items-center gap-2 text-sm disabled:opacity-50"
                      >
                        Reject
                      </button>
                      <button
                        type="button"
                        onClick={handleApprove}
                        disabled={busy}
                        className="px-8 py-2.5 rounded-lg bg-primary text-primary-foreground font-bold hover:shadow-[0_0_15px_rgba(173,198,255,0.4)] transition-all flex items-center gap-2 text-sm ml-auto"
                      >
                        Approve
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-grow flex flex-col items-center justify-center text-muted-foreground">
              <ShieldCheck className="w-12 h-12 text-muted-foreground/30 mb-2" />
              <p className="text-sm font-semibold">Select an item from the queue to review</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={Boolean(activeImage)} onOpenChange={(open) => { if (!open) setActiveImage(null); }}>
        {activeImage && <DialogContent className="max-w-[94vw] border-0 bg-black/90 p-4 text-white shadow-2xl sm:max-w-[94vw]"><DialogHeader className="sr-only"><DialogTitle>Submitted image preview</DialogTitle><DialogDescription>Enlarged view of the selected submitted image.</DialogDescription></DialogHeader><img src={activeImage} alt="Enlarged submitted document" className="mx-auto max-h-[85vh] max-w-full rounded-lg object-contain" /></DialogContent>}
      </Dialog>

      <Dialog open={Boolean(activeDoc)} onOpenChange={(open) => {
        if (!open) {
          if (activeDoc?.url && activeDoc.url.startsWith("blob:")) {
            try { URL.revokeObjectURL(activeDoc.url); } catch (e) {}
          }
          setActiveDoc(null);
        }
      }}>
        {activeDoc && (
          <DialogContent className="max-w-[90vw] sm:max-w-4xl bg-card border-border p-6 shadow-2xl">
            <DialogHeader className="flex flex-row items-center justify-between border-b border-border/40 pb-3">
              <div>
                <DialogTitle className="text-lg font-bold text-foreground capitalize">{activeDoc.title}</DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground mt-0.5">Secure Document Preview</DialogDescription>
              </div>
              <div className="flex items-center gap-2 mr-6">
                <a
                  href={activeDoc.url}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 rounded-lg bg-secondary text-foreground hover:bg-secondary/80 text-xs font-semibold flex items-center gap-1.5 transition-all"
                  title="Open document in a new browser tab"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open in tab
                </a>
                <a
                  href={activeDoc.url}
                  download={activeDoc.fileName}
                  className="px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 text-xs font-semibold flex items-center gap-1.5 transition-all"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download
                </a>
              </div>
            </DialogHeader>

            <div className="mt-4 flex flex-col items-center justify-center min-h-[60vh] max-h-[75vh] overflow-hidden rounded-xl border border-border/60 bg-muted/20 p-2">
              {activeDoc.isPdf ? (
                <iframe
                  src={activeDoc.url}
                  title={activeDoc.title}
                  className="w-full h-[70vh] rounded-lg border-0 bg-white shadow-inner"
                />
              ) : (
                <img
                  src={activeDoc.url}
                  alt={activeDoc.title}
                  className="max-h-[70vh] max-w-full rounded-lg object-contain"
                />
              )}
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}

function IdentityReviewAsset({ url, label }) {
  const safeUrl = getImageUrl(url);
  const isPdf = /^data:application\/pdf/i.test(safeUrl) || /\.pdf(?:$|[?#])/i.test(safeUrl);
  if (!safeUrl) {
    return (
      <div className="overflow-hidden rounded-xl border border-dashed border-border bg-muted/20">
        <div className="flex aspect-video flex-col items-center justify-center text-muted-foreground">
          <Landmark className="mb-2 h-10 w-10" aria-hidden="true" />
          <span className="text-xs font-bold">Not submitted or unavailable</span>
        </div>
        <span className="block p-3 text-xs font-bold text-foreground">{label}</span>
      </div>
    );
  }
  return (
    <button type="button" onClick={() => openPrivateDocument(url)} className="group overflow-hidden rounded-xl border border-border bg-muted/20 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer">
      {isPdf ? (
        <div className="flex aspect-video flex-col items-center justify-center text-primary"><Landmark className="mb-2 h-10 w-10" aria-hidden="true" /><span className="text-xs font-bold">Open protected PDF</span></div>
      ) : (
        <img src={safeUrl} alt={`Submitted ${label.toLowerCase()}`} loading="lazy" decoding="async" className="aspect-video w-full object-cover transition group-hover:scale-[1.02]" />
      )}
      <span className="flex items-center justify-between p-3 text-xs font-bold text-foreground">{label}<ExternalLink className="h-3.5 w-3.5 text-primary" aria-hidden="true" /></span>
    </button>
  );
}

function SoldReviewPanel({ property, fallback }) {
  const review = soldReviewFor(property);
  const fallbackReview = soldReviewFor(fallback);
  const data = Object.keys(review).length ? review : fallbackReview;
  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4" data-testid="sold-review-panel">
      <h4 className="text-xs font-bold text-amber-700 uppercase tracking-widest mb-3">Sold verification request</h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
        <div><p className="text-muted-foreground">Previous Status</p><p className="font-bold text-foreground">{prettyLabel(data.previous_status || "PUBLISHED")}</p></div>
        <div><p className="text-muted-foreground">Requested Role</p><p className="font-bold text-foreground uppercase">{data.requested_role || "—"}</p></div>
        <div><p className="text-muted-foreground">Requested By</p><p className="font-mono text-foreground truncate">{data.requested_by || "—"}</p></div>
        <div><p className="text-muted-foreground">Requested At</p><p className="font-semibold text-foreground">{data.requested_at ? new Date(data.requested_at).toLocaleString("en-IN") : "—"}</p></div>
        <div className="col-span-2 md:col-span-4"><p className="text-muted-foreground">Reason</p><p className="text-foreground">{data.reason || "No reason provided"}</p></div>
      </div>
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div className="border border-dashed border-border rounded-2xl py-16 text-center bg-card">
      <ShieldCheck className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
      <p className="text-muted-foreground">{msg}</p>
    </div>
  );
}
